/**
 * Manages the `overflow` style on the `#root` element for mobile viewports
 * when one or more dialogs are open. This is necessary to prevent the
 * underlying page from scrolling when a dialog is active.
 *
 * It uses a single `MutationObserver` to efficiently track the state of all
 * dialogs (`[role="dialog"]`) on the page. A reference count (`managerCount`)
 * ensures that the observer is only active when at least one dialog component
 * that needs this behavior is mounted.
 *
 * - `startManaging()`: Increments the count and starts the observer if it's the first manager.
 * - `stopManaging()`: Decrements the count and cleans up the observer when the last manager unmounts.
 */

// --- Overflow Manager ---
let managerCount = 0;
let observer: MutationObserver | null = null;

const isMobile = () => window.matchMedia("(max-width: 768px)").matches;

function applyRootOverflowHidden() {
  if (!isMobile()) return;

  const root = document.getElementById("root");
  if (!root) return;

  const openDialogs = document.querySelectorAll('[role="dialog"][data-state="open"]');
  if (openDialogs.length > 0 && !root.hasAttribute("data-original-overflow")) {
    root.setAttribute("data-original-overflow", root.style.overflow);
    root.style.overflow = "hidden";
  }
}

function restoreRootOverflow() {
  const root = document.getElementById("root");
  if (!root) return;

  const openDialogs = document.querySelectorAll('[role="dialog"][data-state="open"]');
  if (!isMobile() || openDialogs.length === 0) {
    if (root.hasAttribute("data-original-overflow")) {
      root.style.overflow = root.getAttribute("data-original-overflow") || "";
      root.removeAttribute("data-original-overflow");
    }
  }
}

const handleStateChange = () => {
  applyRootOverflowHidden();
  restoreRootOverflow();
};

const handleResize = () => {
  handleStateChange();
};

export function startManaging() {
  managerCount++;
  if (observer) return;

  handleStateChange();

  observer = new MutationObserver(handleStateChange);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-state"],
  });
  window.addEventListener("resize", handleResize);
}

export function stopManaging() {
  managerCount--;
  if (managerCount === 0 && observer) {
    observer.disconnect();
    observer = null;
    window.removeEventListener("resize", handleResize);
    restoreRootOverflow();
  }
}

/**
 * Force-reset all module state. Used by test cleanup (e.g. Storybook beforeEach)
 * to recover from stories that crash after startManaging() but before stopManaging().
 */
export function resetForTesting() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  window.removeEventListener("resize", handleResize);
  managerCount = 0;
  restoreRootOverflow();
}
